// AddFacilityModal.jsx - Simplified for deployment
import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Select, Input, Upload, Button, Tag } from 'antd';
import { ImCancelCircle } from 'react-icons/im';
import { toast } from 'react-toastify';
import { InboxOutlined, PlusOutlined, DeleteOutlined, EnvironmentOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { BeatLoader } from "react-spinners";
import { getAllFacilityTags, getAllFacilityServices, getAllFacilityTypes, createFacility, updateFacilityData, getAllInsurances } from '../../../services/facilities';
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';
import { firebaseApp } from '../../../utils/firebaseConfig';
import './AddFacilityModal.css';

const { Option } = Select;
const { Dragger } = Upload;

// Validation schema
const validationSchema = Yup.object().shape({
  facilityName: Yup.string().required('Facility name is required'),
  facilityType: Yup.string().required('Facility type is required'),
  coordinates: Yup.object().shape({
    latitude: Yup.number().required('Latitude is required'),
    longitude: Yup.number().required('Longitude is required')
  }),
  insurances: Yup.array().min(1, 'At least one insurance is required'),
  facilityTags: Yup.array().min(1, 'At least one tag is required'),
  address: Yup.object().shape({
    street: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    area: Yup.string()
  }),
  services: Yup.array().min(1, 'At least one service is required'),
  servicesFr: Yup.array(),
  phone1: Yup.string().required('Primary phone is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  openingHours: Yup.object().shape({
    monday: Yup.string().required('Monday hours required'),
    tuesday: Yup.string().required('Tuesday hours required'),
    wednesday: Yup.string().required('Wednesday hours required'),
    thursday: Yup.string().required('Thursday hours required'),
    friday: Yup.string().required('Friday hours required'),
    saturday: Yup.string(),
    sunday: Yup.string()
  })
});

function AddFacilityModal({ onClose, onContentAdded, editData }) {
  // State variables
  const [loading, setLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableFrenchTags, setAvailableFrenchTags] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableServicesFr, setAvailableServicesFr] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [facilityTypesFr, setFacilityTypesFr] = useState([]);
  const [allInsuranceData, setAllInsuranceData] = useState([]);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState('');
  const [facilityImages, setFacilityImages] = useState([]);
  const [facilityImagePreviews, setFacilityImagePreviews] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const isEditMode = !!editData;

  // Initial values
  const initialValues = isEditMode ? {
    ...editData,
    servicesFr: editData.servicesFr || [],
    facilityTypeFr: editData?.facilityTypeFr || '',
    coordinates: editData.coordinates || { latitude: '', longitude: '' },
    address: editData.address || { street: '', city: '', area: '', postalCode: '' },
    facilityTagsFr: editData.facilityTagsFr || [],
    insurances: editData.insurances || [],
    facilityImages: editData.facilityImages || [],
    openingHours: editData.openingHours || {
      monday: '09:00-17:00',
      tuesday: '09:00-17:00',
      wednesday: '09:00-17:00',
      thursday: '09:00-17:00',
      friday: '09:00-17:00',
      saturday: '09:00-14:00',
      sunday: 'Closed'
    },
  } : {
    facilityName: '',
    facilityType: '',
    facilityTypeFr: '',
    facilityIcon: '',
    facilityImages: [],
    coordinates: {
      latitude: '',
      longitude: ''
    },
    facilityTags: [],
    facilityTagsFr: [],
    insurances: [],
    openingHours: {
      monday: '09:00-17:00',
      tuesday: '09:00-17:00',
      wednesday: '09:00-17:00',
      thursday: '09:00-17:00',
      friday: '09:00-17:00',
      saturday: '09:00-14:00',
      sunday: 'Closed'
    },
    address: {
      street: '',
      city: '',
      area: '',
      postalCode: ''
    },
    services: [],
    servicesFr: [],
    phone1: '',
    phone2: '',
    email: ''
  };

  // Set up edit mode
  useEffect(() => {
    if (isEditMode && editData) {
      if (editData.facilityIcon) {
        setIconPreview(editData.facilityIcon);
      }
      
      if (editData.facilityImages && editData.facilityImages.length > 0) {
        const previews = editData.facilityImages.map(img => img.src);
        setFacilityImagePreviews(previews);
        setFacilityImages(editData.facilityImages);
      }
    }
  }, [editData, isEditMode]);

  // Upload icon
  const uploadIcon = async () => {
    if (!iconFile) return '';
    const storage = getStorage(firebaseApp);
    const storageRef = ref(storage, `facility-icons/${Date.now()}-${iconFile.name}`);
    const snapshot = await uploadBytes(storageRef, iconFile);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  };

  // Upload facility images
  const uploadFacilityImages = async () => {
    if (!facilityImages.length) return [];
    
    setImageUploading(true);
    const storage = getStorage(firebaseApp);
    const uploadPromises = facilityImages.map(async (file, index) => {
      if (file.src) return file;
      
      const storageRef = ref(storage, `facility-images/${Date.now()}-${index}-${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return { type: 'image', src: downloadURL };
    });
  
    const uploadedImages = await Promise.all(uploadPromises);
    setImageUploading(false);
    return uploadedImages;
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      let iconUrl = values.facilityIcon;
      if (iconFile) {
        iconUrl = await uploadIcon();
      }

      const uploadedImages = await uploadFacilityImages();
      
      const facilityData = {
        ...values,
        facilityIcon: iconUrl,
        facilityImages: uploadedImages
      };

      if (isEditMode) {
        await updateFacilityData(editData?.id, facilityData);
        toast.success('Facility updated successfully');
      } else {
        await createFacility({ ...facilityData }); 
        toast.success('Facility added successfully');
      }

      onContentAdded();
      onClose();
    } catch (error) {
      console.error('Error in facility submission:', error);
      toast.error(isEditMode ? 'Error updating facility' : 'Error adding facility');
    } finally {
      setLoading(false);
    }
  };

  // Handle adding facility images
  const handleAddFacilityImage = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      const newPreviews = [];
      
      newFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          newPreviews.push(e.target?.result);
          if (newPreviews.length === newFiles.length) {
            setFacilityImagePreviews([...facilityImagePreviews, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      });
      
      setFacilityImages([...facilityImages, ...newFiles]);
    }
  };

  // Remove image
  const removeImage = (index, setFieldValue, values) => {
    const newImages = [...facilityImages];
    newImages.splice(index, 1);
    setFacilityImages(newImages);
    
    const newPreviews = [...facilityImagePreviews];
    newPreviews.splice(index, 1);
    setFacilityImagePreviews(newPreviews);
    
    if (values.facilityImages && values.facilityImages.length) {
      const updatedFacilityImages = [...values.facilityImages];
      if (index < updatedFacilityImages.length) {
        updatedFacilityImages.splice(index, 1);
        setFieldValue('facilityImages', updatedFacilityImages);
      }
    }
  };

  // Fetch data
  const fetchTags = () => {
    getAllFacilityTags().then((res) => {
        const englishTags = res?.data?.data?.map(({ name }) => name) || [];
        const frenchTags = res?.data?.data?.map((tag) => tag.nameFr).filter(Boolean) || [];
        
        setAvailableTags(englishTags);
        setAvailableFrenchTags(frenchTags);
        setLoading(false);
      });
  };

  const fetchInsurances = async () => {
    const res = await getAllInsurances();
    setAllInsuranceData(res?.data?.data || []);
  };

  const fetchServices = () => {
    getAllFacilityServices().then((res) => {
        const englishServices = res?.data?.data?.map(({ name }) => name) || [];
        const frenchServices = res?.data?.data?.map((service) => service.nameFr).filter(Boolean) || [];
        
        setAvailableServices(englishServices);
        setAvailableServicesFr(frenchServices);
        setLoading(false);
    });
  };

  const fetchFacilityTypes = async () => {
    getAllFacilityTypes().then((res) => {
        const types = res?.data?.data || [];
        const englishTypes = types.map((type) => ({
          name: type.name,
          id: type.id
        }));
        const frenchTypes = types.map((type) => ({
          name: type.nameFr,
          englishName: type.name,
          id: type.id
        })).filter((type) => type.name);
        
        setFacilityTypes(englishTypes);
        setFacilityTypesFr(frenchTypes);
    });
  };

  // Upload props
  const uploadProps = {
    accept: 'image/*',
    beforeUpload: (file) => {
      setIconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result);
      };
      reader.readAsDataURL(file);
      return false;
    },
    maxCount: 1,
    showUploadList: false
  };

  // Custom tag render function
  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    return (
      <Tag 
        className="custom-tag"
        closable={closable}
        onClose={onClose}
      >
        {label}
      </Tag>
    );
  };

  // Fetch all required data
  useEffect(() => {
    fetchTags();
    fetchServices();
    fetchFacilityTypes();
    fetchInsurances();
  }, []);

  return (
    <div className="facility-modal">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Facility' : 'Add New Facility'}</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form className="form-container">
                <div className="form-group">
                  <label>Facility Name</label>
                  <Input
                    name="facilityName"
                    onChange={handleChange}
                    value={values.facilityName}
                    className="custom-input"
                    placeholder="Enter facility name"
                  />
                  {errors.facilityName && touched.facilityName && (
                    <div className="error-message">{errors.facilityName}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Facility Type (English)</label>
                  <Select
                    className="custom-select"
                    onChange={(value) => setFieldValue('facilityType', value)}
                    value={values.facilityType}
                    placeholder="Select facility type"
                  >
                    {facilityTypes?.map((type) => (
                      <Option key={type.id} value={type.name}>
                        {type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                      </Option>
                    ))}
                  </Select>
                  {errors.facilityType && touched.facilityType && (
                    <div className="error-message">{errors.facilityType}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Facility Type (French)</label>
                  <Select
                    className="custom-select"
                    onChange={(value) => setFieldValue('facilityTypeFr', value)}
                    value={values.facilityTypeFr}
                    placeholder="Sélectionner le type d'établissement"
                  >
                    {facilityTypesFr?.map((type) => (
                      <Option key={type.id} value={type.name}>
                        {type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div className="form-group">
                  <label>Facility Tags (English)</label>
                  <Select
                    mode="multiple"
                    className="custom-select"
                    placeholder="Select English tags"
                    onChange={(value) => setFieldValue('facilityTags', value)}
                    value={values.facilityTags}
                    tagRender={tagRender}
                    optionFilterProp="children"
                  >
                    {availableTags.map((tag) => (
                      <Option key={tag} value={tag}>{tag}</Option>
                    ))}
                  </Select>
                  {errors.facilityTags && touched.facilityTags && (
                    <div className="error-message">{errors.facilityTags}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Facility Tags (French)</label>
                  <Select
                    mode="multiple"
                    className="custom-select"
                    placeholder="Sélectionner les tags français"
                    onChange={(value) => setFieldValue('facilityTagsFr', value)}
                    value={values.facilityTagsFr}
                    tagRender={tagRender}
                    optionFilterProp="children"
                  >
                    {availableFrenchTags.map((tag) => (
                      <Option key={tag} value={tag}>{tag}</Option>
                    ))}
                  </Select>
                </div>
                
                <div className="form-group">
                  <label>Insurances</label>
                  <Select
                    mode="multiple"
                    className="custom-select"
                    placeholder="Select insurances accepted"
                    onChange={(value) => setFieldValue('insurances', value)}
                    value={values.insurances}
                    tagRender={tagRender}
                    optionFilterProp="children"
                  >
                    {allInsuranceData.map((insurance) => (
                      <Option key={insurance.id} value={insurance.name}>
                        {insurance.name}
                      </Option>
                    ))}
                  </Select>
                  {errors.insurances && touched.insurances && (
                    <div className="error-message">{errors.insurances}</div>
                  )}
                </div>

                {/* Add Icon Upload */}
                <div className="form-group">
                  <label>Facility Icon</label>
                  <Dragger {...uploadProps} className="icon-uploader">
                    {iconPreview ? (
                      <img src={iconPreview} alt="Icon preview" className="icon-preview" />
                    ) : (
                      <>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag icon to upload</p>
                      </>
                    )}
                  </Dragger>
                  {errors.facilityIcon && touched.facilityIcon && (
                    <div className="error-message">{errors.facilityIcon}</div>
                  )}
                </div>

                {/* Facility Images Upload */}
                <div className="form-group">
                  <label>Facility Images</label>
                  <div className="facility-images-container">
                    <div className="facility-images-preview">
                      {facilityImagePreviews.map((preview, index) => (
                        <div key={index} className="facility-image-item">
                          <img src={preview} alt={`Facility ${index}`} className="facility-image-preview" />
                          <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />} 
                            onClick={() => removeImage(index, setFieldValue, values)}
                            className="remove-image-btn"
                          />
                        </div>
                      ))}
                      <div className="add-image-container">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleAddFacilityImage}
                          id="facility-images-input"
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="facility-images-input" className="add-image-btn">
                          <PlusOutlined /> Add Image
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Opening Hours Section */}
                <div className="opening-hours-section">
                  <h3>Opening Hours</h3>
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                    <div className="form-group" key={day}>
                      <label>{day.charAt(0).toUpperCase() + day.slice(1)}</label>
                      <Select
                        className="custom-select"
                        onChange={(value) => setFieldValue(`openingHours.${day}`, value)}
                        value={values.openingHours[day]}
                      >
                        <Option value="Closed">Closed</Option>
                        <Option value="09:00-17:00">09:00 - 17:00</Option>
                        <Option value="09:00-18:00">09:00 - 18:00</Option>
                        <Option value="08:00-20:00">08:00 - 20:00</Option>
                        <Option value="24/7">24/7</Option>
                        <Option value="custom">Custom Hours</Option>
                      </Select>
                      {errors.openingHours?.[day] && touched.openingHours?.[day] && (
                        <div className="error-message">{errors.openingHours[day]}</div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="form-group">
                  <label>Services (English)</label>
                  <Select
                    mode="multiple"
                    className="custom-select"
                    placeholder="Select services"
                    onChange={(value) => setFieldValue('services', value)}
                    value={values.services}
                    tagRender={tagRender}
                    optionFilterProp="children"
                  >
                    {availableServices.map((service) => (
                      <Option key={service} value={service}>{service}</Option>
                    ))}
                  </Select>
                  {errors.services && touched.services && (
                    <div className="error-message">{errors.services}</div>
                  )}
                </div>

                <div className="form-group">
                  <label>Services (French)</label>
                  <Select
                    mode="multiple"
                    className="custom-select"
                    placeholder="Select French services"
                    onChange={(value) => setFieldValue('servicesFr', value)}
                    value={values.servicesFr}
                    tagRender={tagRender}
                    optionFilterProp="children"
                  >
                    {availableServicesFr.map((service) => (
                      <Option key={service} value={service}>{service}</Option>
                    ))}
                  </Select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Latitude</label>
                    <Input
                      name="coordinates.latitude"
                      onChange={handleChange}
                      value={values.coordinates.latitude}
                      type="number"
                      className="custom-input"
                    />
                    {errors.coordinates?.latitude && touched.coordinates?.latitude && (
                      <div className="error-message">{errors.coordinates.latitude}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <Input
                      name="coordinates.longitude"
                      onChange={handleChange}
                      value={values.coordinates.longitude}
                      type="number"
                      className="custom-input"
                    />
                    {errors.coordinates?.longitude && touched.coordinates?.longitude && (
                      <div className="error-message">{errors.coordinates.longitude}</div>
                    )}
                  </div>
                </div>

                {/* Address Fields */}
                <div className="address-section">
                  <h3>Address</h3>
                  <div className="form-group">
                    <Input
                      name="address.street"
                      onChange={handleChange}
                      value={values.address.street}
                      placeholder="Street"
                      prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />}
                      className="custom-input"
                    />
                    {errors.address?.street && touched.address?.street && (
                      <div className="error-message">{errors.address.street}</div>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <Input
                        name="address.city"
                        onChange={handleChange}
                        value={values.address.city}
                        placeholder="City"
                        className="custom-input"
                      />
                      {errors.address?.city && touched.address?.city && (
                        <div className="error-message">{errors.address.city}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <Input
                        name="address.area"
                        onChange={handleChange}
                        value={values.address.area}
                        placeholder="Area"
                        className="custom-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="contact-section">
                  <h3>Contact Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <Input
                        name="phone1"
                        onChange={handleChange}
                        value={values.phone1}
                        placeholder="Primary Phone"
                        prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                        className="custom-input"
                      />
                      {errors.phone1 && touched.phone1 && (
                        <div className="error-message">{errors.phone1}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <Input
                        name="phone2"
                        onChange={handleChange}
                        value={values.phone2}
                        placeholder="Secondary Phone (Optional)"
                        prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                        className="custom-input"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <Input
                      name="email"
                      onChange={handleChange}
                      value={values.email}
                      placeholder="Email"
                      prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                      className="custom-input"
                    />
                    {errors.email && touched.email && (
                      <div className="error-message">{errors.email}</div>
                    )}
                  </div>
                </div>

                <div className="button-container">
                  {loading || imageUploading ? (
                    <BeatLoader color="#322659" />
                  ) : (
                    <button type="submit" className="submit-button">
                      {isEditMode ? 'Update Facility' : 'Add Facility'}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
}

export default AddFacilityModal;